'use client'

import { builder } from '@builder.io/react';
import { Cart, CartLineItem, Product, ProductVariant } from 'lib/bigcommerce/types';
import { Customer } from 'lib/bigcommerce/types/customer';
import { BCLineItem, BCOrder } from 'lib/rest-service/types';
import { extractConsignments, extractLineItems, getSalePrice, tryParseMoney } from 'lib/utils';
import * as deviceDetect from 'react-device-detect';
import { CustomAnalytics, FunnelData, cleanQueryParams } from 'shared-react-components';

const eventPrefix = '4p_';

export const baseGtmData = (props: { customer?: Customer, sessionId: string, funnelData?: FunnelData }) => {
  if (typeof window === 'undefined') return {};

  const { customer, sessionId, funnelData } = props;

  const device = getDevice();
  const state = CustomAnalytics.getState();
  const queryParams = Object.fromEntries(cleanQueryParams(window.location.search));
  const sessionQueryParams = Object.keys(state.page?.queryParams ?? {}).length !== 0 ? state.page?.queryParams : null;
  const paramsContext = {
    ...(state?.sessionId ? {sessionId: state?.sessionId}: {}),
    ...(sessionQueryParams ?? queryParams)
  };

  const baseData = {
    device: {
      name: device,
      manufacturer: deviceDetect.isMobile ? deviceDetect.mobileVendor : deviceDetect.browserName,
      type: deviceDetect.isMobile ? deviceDetect.deviceType : 'desktop',
      resolution: {
        width: window.screen.availWidth,
        height: window.screen.availHeight
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      encoding: 'UTF-8',
      language: window.navigator.language,
      colors: `${window.screen.colorDepth}-bit`
    },
    browser: {
      manufacturer: device,
      name: deviceDetect.browserName,
      majorVersion: deviceDetect.browserVersion,
      version: deviceDetect.fullBrowserVersion
    },
    os: {
      majorVersion: deviceDetect.osName,
      name: deviceDetect.osName,
      version: deviceDetect.osVersion
    },
    scrollPosition: window.scrollY.toString(),
    customer: {
      id: customer?.entityId.toString(),
      email: customer?.email,
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      phone: customer?.phone
    },
    sessionId,
    channel_id: isFunnel(funnelData) ? 'funnel' : 'storefront',
    funnelData: {
      productLine: funnelData?.productLine,
      funnelName: funnelData?.funnelName,
      stepName: funnelData?.stepName,
      type: funnelData?.type,
    },
    sessionQueryParams: paramsContext
  };
  sendGtmEvent(baseData);
  return baseData;
};

type PageViewProps = {
  prevPathname: string;
  prevSearchParams: string;
  pathname: string;
  searchParams: string;
  funnelData?: FunnelData; 
};
export const pageView = (props: PageViewProps) => {
  if (typeof window === 'undefined') return {};

  const { prevPathname, prevSearchParams, pathname, searchParams } = props;

  const pageViewEvent = {
    event: `${eventPrefix}page_view`,
    channel_id: isFunnel(props.funnelData) ? 'funnel' : 'storefront',
    urlPath: pathname,
    queryParams: searchParams,
    oldUrlPath: prevPathname,
    oldQueryParams: prevSearchParams,
    scrollPosition: window.scrollY.toString(),
  };
  sendGtmEvent(pageViewEvent);
  return pageViewEvent;
};

export const viewCart = ({ cart }: { cart: Cart }) => {
  if (typeof window === 'undefined') return {};

  const couponCode = cart.coupons.at(0)?.code;

  const viewCartEvent = {
    event: `${eventPrefix}view_cart`,
    ecommerce: {
      currency: cart.cost.discountedAmount.currencyCode,
      channel_id: 'storefront',
      value: stringToFixed(cart.cost.discountedAmount.amount),
      discount: stringToFixed(cart.cost.discountAmount.amount),
      coupon: couponCode,
      items: cart.lines.map((lineItem, index) => cartLineItemToGtmItem({ lineItem, index, couponCode })),
      customer: {
        id: cart.customerId?.toString(),
        email: cart.email,
      },
      scrollPosition: window.scrollY.toString(),
      factor_13: numberToFixed(gtmGpmTotal({ lineItems: cart.lines })),
    }
  }
  // Clear the previous ecommerce object.
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(viewCartEvent);
  return viewCartEvent;
}

type AddToCartProps = { cart: Cart, lineItem: CartLineItem, quantity: number, itemListId?: string, itemListName?: string };
export const addToCart = ({ cart, lineItem, quantity, itemListId, itemListName }: AddToCartProps) => {
  if (typeof window === 'undefined') return {};

  const couponCode = cart.coupons.at(0)?.code;
  const salePrice = tryParseMoney((lineItem.cost.salePrice ?? lineItem.cost.listPrice).amount);
  const value = salePrice ? (salePrice * quantity) : undefined;
  const gpmPrice = tryParseMoney(lineItem.cost.gpm?.amount);
  const gpm = gpmPrice ? (gpmPrice * quantity) : undefined;
  const addToCartEvent = {
    event: `${eventPrefix}add_to_cart`,
    ecommerce: {
      currency: lineItem.cost.listPrice.currencyCode,
      channel_id: 'storefront',
      value: numberToFixed(value),
      coupon: couponCode,
      items: [cartLineItemToGtmItem({ lineItem, index: 0, couponCode, quantity, itemListId, itemListName })],
      customer: {
        id: cart.customerId?.toString(),
        email: cart.email,
      },
      scrollPosition: window.scrollY.toString(),
      factor_13: numberToFixed(gpm),
    }
  }
  // Clear the previous ecommerce object.
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(addToCartEvent);
  return addToCartEvent;
}

export const funnelAddToCart = ({ product, sku, quantity = 1 }: { product?: Product, sku?: string, quantity?: number }) => {
  if (typeof window === 'undefined') return {};
  if (!product) return {};

  const variant = product.variants.find(v => v.sku === sku) ?? product.variants.at(0);
  const salePrice = getSellingPrice(product, variant);
  const amount = tryParseMoney(salePrice.amount);
  const value = amount ? (amount * quantity) : undefined;

  const gpmPrice = variant?.gpm ?? product?.gpm;
  const gpmAmount = tryParseMoney(gpmPrice?.amount);
  const gpm = gpmAmount ? (gpmAmount * quantity) : undefined;

  const addToCartEvent = {
    event: `${eventPrefix}add_to_cart`,
    ecommerce: {
      currency: salePrice.currencyCode ?? 'USD',
      channel_id: 'funnel',
      value: numberToFixed(value),
      coupon: undefined,
      items: [productToGtmItem({ product, index: 0, variant })],
      scrollPosition: window.scrollY.toString(),
      factor_13: numberToFixed(gpm),
    }
  }
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(addToCartEvent);
  return addToCartEvent;
}

export const removeFromCart = ({ cart, lineItem, quantity }: { cart: Cart, lineItem: CartLineItem, quantity: number }) => {
  if (typeof window === 'undefined') return {};

  const couponCode = cart.coupons.at(0)?.code;
  const salePrice = tryParseMoney((lineItem.cost.salePrice ?? lineItem.cost.listPrice).amount);
  const value = salePrice ? (salePrice * quantity) : undefined;
  const gpmPrice = tryParseMoney(lineItem.cost.gpm?.amount);
  const gpm = gpmPrice ? (gpmPrice * quantity) : undefined;
  const removeFromCartEvent = {
    event: `${eventPrefix}remove_from_cart`,
    ecommerce: {
      currency: lineItem.cost.listPrice.currencyCode,
      channel_id: 'storefront',
      value: numberToFixed(value),
      coupon: couponCode,
      items: [cartLineItemToGtmItem({ lineItem, index: 0, couponCode, quantity })],
      customer: {
        id: cart.customerId?.toString(),
        email: cart.email,
      },
      scrollPosition: window.scrollY.toString(),
      factor_13: numberToFixed(gpm),
    }
  }
  // Clear the previous ecommerce object.
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(removeFromCartEvent);
  return removeFromCartEvent;
}

export const addToWishlist = ({ product, sku, customer }: { product: Product, sku?: string, customer?: Customer }) => {
  if (typeof window === 'undefined') return {};

  const variant = product.variants.find(v => v.sku === sku) ?? product.variants.at(0);
  const salePrice = getSellingPrice(product, variant);
  const addToWishlistEvent = {
    event: `${eventPrefix}add_to_wishlist`,
    ecommerce: {
      currency: salePrice.currencyCode,
      channel_id: 'storefront',
      value: stringToFixed(salePrice.amount),
      coupon: null,
      items: [productToGtmItem({ product, index: 0, variant })],
      customer: {
        id: customer?.entityId.toString(),
        email: customer?.email,
      },
      scrollPosition: window.scrollY.toString(),
    }
  }
  // Clear the previous ecommerce object.
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(addToWishlistEvent);
  return addToWishlistEvent;
}

export const search = ({ searchTerm }: { searchTerm: string }) => {
  if (typeof window === 'undefined') return {};

  const searchEvent = {
    event: `${eventPrefix}search`,
    search_term: searchTerm
  }
  sendGtmEvent(searchEvent);
  return searchEvent;
}

export const viewItemList = ({ product, sku, itemListId, itemListName }: { product: Product, sku?: string, itemListId?: string, itemListName?: string }) => {
  if (typeof window === 'undefined') return {};

  const variant = product.variants.find(v => v.sku === sku) ?? product.variants.at(0);
  const viewItemListEvent = {
    event: `${eventPrefix}view_item_list`,
    ecommerce: {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: [productToGtmItem({ product, index: 0, variant })],
    },
    scrollPosition: window.scrollY.toString(),
  }

  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(viewItemListEvent);
  return viewItemListEvent;
}

export const viewItem = ({ product, sku }: { product?: Product, sku?: string }) => {
  if (typeof window === 'undefined') return {};
  if (!product) return {};

  const variant = product.variants.find(v => v.sku === sku) ?? product.variants.at(0);
  const salePrice = getSellingPrice(product, variant);
  const viewItemEvent = {
    event: `${eventPrefix}view_item`,
    ecommerce: {
      currency: salePrice.currencyCode,
      value: stringToFixed(salePrice.amount),
      items: [productToGtmItem({ product, index: 0, variant })],
    },
    scrollPosition: window.scrollY,
  }
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(viewItemEvent);
  return viewItemEvent;
}

type PurchaseProps = { orderId: number, order?: BCOrder, product?: Product, itemListId?: string, itemListName?: string, funnelData?: FunnelData };
export const purchase = ({ orderId, order, product, itemListId, itemListName, funnelData }: PurchaseProps) => {
  if (typeof window === 'undefined') return;

  const { shipping, downloads } = extractConsignments(order?.consignments);
  const shippingLineItems = extractLineItems(shipping);
  const downloadLineItems = extractLineItems(downloads);

  const lineItem = shippingLineItems?.at(0) ?? downloadLineItems?.at(0);
  const variant = product?.variants?.find(v => lineItem?.sku === v.sku);
  const gpm = variant?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString() ?? product?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString();
  const gpmPrice = tryParseMoney(gpm);
  const quantity = lineItem?.quantity ?? 1;
  const gpmTotal = gpmPrice ? (gpmPrice * quantity) : undefined;
  const purchaseValue = stringToFixed(order?.total_inc_tax);

  const purchaseEvent = {
    event: `${eventPrefix}purchase`,
    ecommerce: {
      currency: order?.currency_code ?? 'USD',
      channel_id: isFunnel(funnelData) ? 'funnel' : 'storefront',
      funnelData: {
        productLine: funnelData?.productLine,
        funnelName: funnelData?.funnelName,
        stepName: funnelData?.stepName,
        type: funnelData?.type,
      },
      value: purchaseValue,
      transaction_id: order?.id ?? orderId,
      coupon: undefined,
      shipping: stringToFixed(order?.shipping_cost_inc_tax),
      tax: stringToFixed(order?.total_tax),
      items: [orderLineItemToGtmItem({ lineItem, product, index: 0, itemListId, itemListName })],
      customer: {
        id: order?.customer_id?.toString(),
        email: order?.billing_address?.email ?? shipping.at(0)?.email
      },
      scrollPosition: window.scrollY.toString(),
      factor_13: numberToFixed(gpmTotal),
    }
  }

  // Send conversion tracking to Builder if email does not contain testing credentials
  const email = order?.billing_address?.email ?? shipping.at(0)?.email
  if (email?.indexOf("@test.com") !== -1 || email?.indexOf("@4patriots.com") !== -1) {
    console.log('Test purchase ', numberToFixed(gpmTotal), ', not sent to Builder Insights')
  } else {
    builder.trackConversion(numberToFixed(gpmTotal));
  }
  // Clear the previous ecommerce object.
  sendGtmEvent({ ecommerce: null });
  sendGtmEvent(purchaseEvent);
  return purchaseEvent;
}

type GtmItem = {
  index?: number;
  item_id?: string | null;
  item_name?: string | null;
  item_variant_id?: string | null;
  coupon?: string | null;
  discount?: number | null;
  item_brand?: string | null;
  item_category?: string | null;
  item_category2?: string | null;
  item_category3?: string | null;
  item_category4?: string | null;
  item_category5?: string | null;
  item_list_id?: string | null;
  item_list_name?: string | null;
  item_variant?: string | null;
  price?: number | null;
  retail?: number | null;
  cost?: number | null;
  factor_13?: number | null;
  quantity?: number;
}

type CartLineItemProps = { lineItem: CartLineItem, index: number, couponCode?: string, quantity?: number, itemListId?: string, itemListName?: string };
export const cartLineItemToGtmItem = ({ lineItem, index, couponCode, quantity, itemListId, itemListName }: CartLineItemProps): GtmItem => {
  const product = lineItem.merchandise.product;
  const variant = lineItem.merchandise.product.variants.find(v => lineItem.sku === v.sku);
  const salePrice = lineItem.cost.salePrice ?? lineItem.cost.listPrice;

  const selectedOptions = lineItem.merchandise.selectedOptions
    .map(({ name, value }) => `${name}: ${value}`)
    .join(' ');

  return {
    index,
    item_id: lineItem.sku,
    item_name: lineItem.merchandise.title,
    item_variant_id: variant?.sku,
    coupon: couponCode,
    discount: stringToFixed(lineItem.cost.discountAmount?.amount),
    item_brand: product.brand,
    item_category: product.categories.at(0)?.name,
    item_category2: product.categories.at(1)?.name,
    item_category3: product.categories.at(2)?.name,
    item_category4: product.categories.at(3)?.name,
    item_category5: product.categories.at(4)?.name,
    item_list_id: itemListId,
    item_list_name: itemListName,
    item_variant: selectedOptions,
    price: stringToFixed(salePrice.amount),
    retail: lineItem.cost.retailPrice && stringToFixed(lineItem.cost.retailPrice.amount),
    cost: lineItem.cost.costPrice && stringToFixed(lineItem.cost.costPrice.amount),
    factor_13: lineItem.cost.gpm && stringToFixed(lineItem.cost.gpm.amount),
    quantity: quantity ?? lineItem.quantity
  };
};

type OrderLineItemProps = { lineItem?: BCLineItem, product?: Product, index: number, couponCode?: string, itemListId?: string, itemListName?: string };
export const orderLineItemToGtmItem = ({ lineItem, index, couponCode, itemListId, itemListName, product }: OrderLineItemProps): GtmItem => {
  if (!lineItem) {
    return {};
  }

  const variant = product?.variants?.find(v => lineItem.sku === v.sku);
  const costPrice = variant?.productMetafields?.find(m => m.key === 'cost-price')?.value?.toString() ?? product?.productMetafields?.find(m => m.key === 'cost-price')?.value?.toString();
  const gpm = variant?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString() ?? product?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString();
  const selectedOptions = lineItem.product_options?.map(({ name, value }) => `${name}: ${value}`)?.join(' ');
  const discountAmount = lineItem.applied_discounts?.reduce(
    (total, discount) => {
      return total + (tryParseMoney(discount.amount) ?? 0);
    }, 0
  );

  return {
    index,
    item_id: lineItem.sku,
    item_name: lineItem.name,
    item_variant_id: variant?.sku,
    coupon: couponCode,
    discount: numberToFixed(discountAmount),
    item_brand: product?.brand,
    item_category: product?.categories?.at(0)?.name,
    item_category2: product?.categories?.at(1)?.name,
    item_category3: product?.categories?.at(2)?.name,
    item_category4: product?.categories?.at(3)?.name,
    item_category5: product?.categories?.at(4)?.name,
    item_list_id: itemListId,
    item_list_name: itemListName,
    item_variant: selectedOptions,
    price: stringToFixed(lineItem.price_ex_tax),
    retail: stringToFixed(lineItem.base_price),
    cost: stringToFixed(costPrice),
    factor_13: stringToFixed(gpm),
    quantity: lineItem.quantity
  };
};

type ViewItemProps = { product: Product, index: number, variant?: ProductVariant, itemListId?: string, itemListName?: string }
export const productToGtmItem = ({ product, index, variant, itemListId, itemListName }: ViewItemProps): GtmItem => {
  const selectedOptions = variant?.selectedOptions
    .map(({ name, value }) => `${name}: ${value}`)
    .join(' ');
  const salePrice = getSellingPrice(product, variant);

  return {
    index,
    item_id: product.sku,
    item_name: product.title,
    item_variant_id: variant?.sku,
    coupon: null,
    discount: null,
    item_brand: product.brand,
    item_category: product.categories.at(0)?.name,
    item_category2: product.categories.at(1)?.name,
    item_category3: product.categories.at(2)?.name,
    item_category4: product.categories.at(3)?.name,
    item_category5: product.categories.at(4)?.name,
    item_list_id: itemListId,
    item_list_name: itemListName,
    item_variant: selectedOptions,
    price: stringToFixed(salePrice.amount),
    retail: stringToFixed((variant?.retailPrice ?? product.retailPrice)?.amount),
    cost: stringToFixed((variant?.costPrice ?? product.costPrice)?.amount),
    factor_13: stringToFixed((variant?.gpm ?? product.gpm)?.amount),
    quantity: 1
  };
};

// Utility Functions

const isFunnel = (funnelData?: FunnelData) => (funnelData?.productLine?.length ?? 0) > 0;

const stringToFixed = (money?: string) => {
  const parsedMoney = tryParseMoney(money);
  if (parsedMoney) {
    return Number(parsedMoney.toFixed(2));
  }
  return undefined;
};

const numberToFixed = (money?: number) => {
  if (money == undefined) return undefined;
  return Number(money.toFixed(2));
};

const getSellingPrice = (product: Product, variant?: ProductVariant) => {
  const variantPrice = variant && getSalePrice(variant);
  const productPrice = getSalePrice(product);
  return variantPrice ?? productPrice;
}

const gtmGpmTotal = ({ lineItems }: { lineItems: CartLineItem[] }) => {
  const total = lineItems.reduce(
    (total, lineItem) => {
      const gpmString = lineItem.cost.gpm?.amount
      const gpm = tryParseMoney(gpmString) ?? 0;
      return total + (lineItem.quantity * gpm);
    }, 0
  );
  return total;
}

export function getDevice() {
  if (deviceDetect.isAndroid) return 'Android'
  if (deviceDetect.isWinPhone) return 'Windows Phone'
  if (deviceDetect.isMobileSafari) return 'Safari'
  if (deviceDetect.isIOS) return 'iOS'
  if (deviceDetect.isChrome) return 'Chrome'
  if (deviceDetect.isFirefox) return 'Firefox browser'
  if (deviceDetect.isSafari) return 'Safari browser'
  if (deviceDetect.isOpera) return 'Opera browser'
  if (deviceDetect.isIE) return 'Internet Explorer browser'
  if (deviceDetect.isEdge) return 'Edge browser'
  if (deviceDetect.isChromium) return 'Chromium'
  if (deviceDetect.isSmartTV) return 'SmartTv'
}

function sendGtmEvent(payload: any) {
  if (typeof window === 'undefined') return;

  if (window['dataLayer']) {
    window['dataLayer'].push(payload)
  } else {
    console.warn('GA dataLayer does not exist')
  }
}
